export type MagicLayerRectPct = {
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo cargar la imagen"));
    img.src = src;
  });
}

function pctToPx(rect: MagicLayerRectPct, w: number, h: number) {
  const x = Math.round((rect.xPct / 100) * w);
  const y = Math.round((rect.yPct / 100) * h);
  const rw = Math.max(1, Math.round((rect.wPct / 100) * w));
  const rh = Math.max(1, Math.round((rect.hPct / 100) * h));
  return {
    x: Math.max(0, Math.min(w - 1, x)),
    y: Math.max(0, Math.min(h - 1, y)),
    w: Math.min(rw, w - x),
    h: Math.min(rh, h - y),
  };
}

/** Rellena un rectángulo con un blur del entorno (aprox. content-aware simple). */
function fillHoleWithBlur(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  hole: { x: number; y: number; w: number; h: number }
) {
  const pad = Math.max(24, Math.round(Math.max(hole.w, hole.h) * 0.35));
  const sx = Math.max(0, hole.x - pad);
  const sy = Math.max(0, hole.y - pad);
  const sw = Math.min(img.naturalWidth - sx, hole.w + pad * 2);
  const sh = Math.min(img.naturalHeight - sy, hole.h + pad * 2);

  const tmp = document.createElement("canvas");
  tmp.width = Math.max(1, sw);
  tmp.height = Math.max(1, sh);
  const tctx = tmp.getContext("2d");
  if (!tctx) return;

  tctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  tctx.filter = "blur(18px)";
  tctx.drawImage(tmp, 0, 0);
  tctx.filter = "none";

  ctx.drawImage(
    tmp,
    hole.x - sx,
    hole.y - sy,
    hole.w,
    hole.h,
    hole.x,
    hole.y,
    hole.w,
    hole.h
  );
}

/**
 * Extrae un recorte de la imagen como dataURL (capa flotante).
 */
export async function extractMagicLayerPatch(
  imageSrc: string,
  rect: MagicLayerRectPct
): Promise<string> {
  const img = await loadImage(imageSrc);
  const px = pctToPx(rect, img.naturalWidth, img.naturalHeight);
  const canvas = document.createElement("canvas");
  canvas.width = px.w;
  canvas.height = px.h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas no disponible");
  ctx.drawImage(img, px.x, px.y, px.w, px.h, 0, 0, px.w, px.h);
  return canvas.toDataURL("image/png");
}

/**
 * Mueve la región source → dest: rellena el hueco y pega el parche.
 */
export async function applyMagicLayerMove(
  imageSrc: string,
  source: MagicLayerRectPct,
  dest: MagicLayerRectPct
): Promise<Blob> {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas no disponible");

  ctx.drawImage(img, 0, 0);
  const from = pctToPx(source, canvas.width, canvas.height);
  const to = pctToPx(dest, canvas.width, canvas.height);

  const patch = document.createElement("canvas");
  patch.width = from.w;
  patch.height = from.h;
  const pctx = patch.getContext("2d");
  if (!pctx) throw new Error("Canvas no disponible");
  pctx.drawImage(img, from.x, from.y, from.w, from.h, 0, 0, from.w, from.h);

  fillHoleWithBlur(ctx, img, from);
  ctx.drawImage(patch, to.x, to.y, to.w, to.h);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
  );
  if (!blob) throw new Error("No se pudo exportar la imagen");
  return blob;
}
